<template>
  <div v-if="!$config.public.performanceMode.disablePreisvorteileSichern">
    <CouponTeaser
      :active-coupon="activeCoupon"
      :error-status="errorStatus"
      :teaser-heading="teaserHeading"
      :teaser-copy="teaserCopy"
      :heading-tag="teaserHeadingTag"
      :mini-version="miniVersion"
      :medium="medium"
      :large="large"
      :custom-brand-error="customBrandError"
      @submit-coupon="submitCoupon"
      @erase-coupon="eraseCoupon"
    />

    <Hinweis v-if="isValidCoupon" />

    <div v-if="showSingleTeaser && goliathOffers.entries">
      <SingleProductTeaser :featured-offer="goliathOffers.entries[0]" />
    </div>
    <section
      v-if="isValidCoupon && !showSingleTeaser"
      ref="resultsEl"
      class="coupon-check__results"
    >
      <Heading
        text-uppercase
        size="md"
        font-weight="ultra"
        align="center"
        tag="h2"
      >
        {{ pageContent.results.heading }}
      </Heading>
      <Paragraph
        v-if="displayFiltersAndCount"
        align="center"
        is-size-big
        >{{ offersCount }} Artikel</Paragraph
      >
      <FilterAccordion
        v-if="displayFiltersAndCount"
        :aggregations="aggregations"
        :variables="listViewParams"
        :item-urls="itemUrls"
        :hidden-filters="hiddenFilters"
        :current-offers-count="offersCount.value"
        cross-category
        @submit="refreshOffers"
      />
      <GridContainer>
        <OfferTiles
          v-if="hasOffers"
          class="coupon-check__results-offer-tiles"
          :offers="goliathOffers?.entries || []"
          :tag-title="tagTitle"
        />
        <div
          v-else
          class="coupon-check__no-results"
        >
          Für diese Auswahl haben wir leider keine Angebote gefunden.
        </div>
      </GridContainer>
      <div
        v-if="showPagination"
        class="coupon-check__pagination-container"
      >
        <Pagination
          class="coupon-check__pagination"
          :current-page="pagination?.page"
          :total-pages="pagination?.totalPages"
          :url="pageNumber"
          emit-only-click
          @click="paginationChange"
        />
        <PageLengthSelector
          class="coupon-check__page-length"
          :current-page-length="pagination?.size || 48"
          @change="pageLengthChanged($event)"
        />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp } from '#imports';
import { ref, computed, defineComponent, watch } from 'vue';
import { pull } from 'lodash-es';
import { Heading, Paragraph, GridContainer } from '@i22-td-smarthome/component-library';
import CouponTeaser from '@/components/treueshop/coupon-teaser.vue';
import SingleProductTeaser from '@/components/treueshop/single-product-teaser.vue';
import OfferTiles from '~/components/offer-tiles-container.vue';
import Hinweis from '@/components/treueshop/hinweis.vue';
import FilterAccordion from '~/components/filter/filter-accordion.vue';
import PageLengthSelector from '@/components/page-length-selector.vue';
import Pagination from '@/components/pagination.vue';

import fetchOffers from '@/lib/goliath/offers';
import type { PaginationParams } from '@/lib/goliath/goliath.types';

defineComponent({ name: 'CouponCheck' });

const props = defineProps({
  teaserHeading: {
    type: String,
    default: '',
  },
  teaserCopy: {
    type: String,
    default: '',
  },
  teaserHeadingTag: {
    type: String,
    default: 'h1',
  },
  miniVersion: {
    type: Boolean,
  },
  customBrandError: {
    type: String,
    default: '',
  },
  tagTitle: { type: String, default: 'h2' },
  couponBrands: {
    type: Array,
    default: () => [],
  },
  medium: {
    type: Boolean,
  },
  large: {
    type: Boolean,
  },
});

const { $store: store, $router: router, $route: route, $config } = useNuxtApp();
const resultsEl = ref<HTMLElement | null>(null);

const pageContent = {
  results: {
    heading: 'Aktionsangebote',
  },
  teasers: {
    heading: 'Aktuelle Gutschein-Aktionen',
    copy: 'Nutze unsere aktuellen Aktions-Angebote und sichere Dir tolle Preisvorteile!',
  },
};

const itemUrls = ref({
  productCategories: () => '',
  brands: () => '',
  colors: () => '',
  compatibleProductFamilies: () => '',
  productTagSlugs: () => '',
});

// Coupon and Results

const listViewPagination = ref<PaginationParams>({ page: 1, size: 48 });

const couponInfo = computed(() => store.getters['coupon/couponInfo']);

const isCouponType = computed(() => couponInfo.value?.type === 'coupon');

const listViewParams = ref<any>({
  filter: {
    discounted: null,
    productProviders: [],
    brandSlugs: [],
    colorSlugs: [],
    compatibleProductFamilySlugs: [],
    productTagSlugs: [],
    onetimePrice: {},
    productCategoryPaths: [],
    listed: true,
    types: ['TANGIBLE', 'MIXED'],
    productAvailable: true,
    articleNumbers: [],
    blacklistArticleNumbers: [],
  },
  pagination: listViewPagination.value,
  grouped: true,
  isPaginationDefault: true,
});

const goliathOffers = ref<any>({
  entries: [],
  pagination: {
    page: 1,
    size: 48,
    totalCount: 0,
    pagesCount: 1,
  },
});

const activeCoupon = computed(() => couponInfo.value?.couponCode);
const isValidCoupon = computed(() => store.getters['coupon/isValidCoupon']);

const errorStatus = computed(() => store.getters['coupon/errorStatus']);

const compatibleArticleNumbers = computed(() => store.getters['coupon/compatibleArticleNumbers']);

const incompatibleArticleNumbers = computed(
  () => store.getters['coupon/incompatibleArticleNumbers']
);

const compatibleProductProviders = computed(
  () => store.getters['coupon/compatibleProductProviders']
);

const hiddenFilters = computed(() => {
  const filterArray = [];
  if (compatibleProductProviders.value?.length > 0) filterArray.push('providers');
  if (props.couponBrands?.length > 0) filterArray.push('brands');
  if (isCouponType.value) filterArray.push('discounts');
  return filterArray;
});

const userFiltersSet = ref(false);
const showSingleTeaser = computed(() => {
  // don't show single teaser if user filters products by himself
  if (userFiltersSet.value) return false;
  return goliathOffers?.value?.entries?.length === 1;
});

const updateCompatibleProducts = () => {
  listViewParams.value.filter = {
    ...listViewParams.value.filter,
    discounted: isCouponType.value ? false : null,
    articleNumbers: compatibleArticleNumbers.value,
    blacklistArticleNumbers: incompatibleArticleNumbers.value,
    productProviders: compatibleProductProviders.value,
    brandSlugs: props.couponBrands,
  };
};

const submitCoupon = async (couponValue: string) => {
  try {
    if (!$config.public.mode.isProduction) {
      if (couponValue === 'show1product') {
        store.commit('coupon/SET_COUPON', {
          status: 'valid',
          productNumbersWhitelist: ['8718699787059'],
          type: 'coupon',
          couponCode: couponValue,
        });
        return;
      }
      if (couponValue === 'showproducts') {
        store.commit('coupon/SET_COUPON', {
          status: 'valid',
          providersWhitelist: ['EGSolo_Brodos'],
          type: 'coupon',
          couponCode: couponValue,
        });
        return;
      }
    }
    await store.dispatch('coupon/updateCoupon', couponValue.trim());
  } catch (e: any) {
    console.error(e);
  }
};

const resetListViewParams = () => {
  listViewPagination.value = { page: 1, size: 48 };

  listViewParams.value = {
    filter: {
      discounted: null,
      productProviders: [],
      brandSlugs: [],
      colorSlugs: [],
      compatibleProductFamilySlugs: [],
      productTagSlugs: [],
      onetimePrice: {},
      productCategoryPaths: [],
      listed: true,
      types: ['TANGIBLE', 'MIXED'],
      productAvailable: true,
      articleNumbers: [],
      blacklistArticleNumbers: [],
    },
    pagination: listViewPagination.value,
    grouped: true,
    isPaginationDefault: true,
  };
  userFiltersSet.value = false;
};

const eraseCoupon = () => {
  store.dispatch('coupon/clearCoupon');
  goliathOffers.value = {};
  resetListViewParams();
};

const listViewParamsDefault = ref<any>(JSON.stringify(listViewParams.value));

watch(
  [isValidCoupon, activeCoupon],
  () => {
    updateCompatibleProducts();
  },
  { immediate: true }
);

watch(
  [listViewParams, isValidCoupon],
  async () => {
    // The coupon can only be set from the BE, so it is not possible to call it SSR
    if (process.server) return;
    // check, if a filter is set.
    if (listViewParamsDefault.value === JSON.stringify(listViewParams.value)) return;
    try {
      goliathOffers.value = await fetchOffers(listViewParams.value);
    } catch (e: any) {
      console.error(e);
    }
  },
  { immediate: true, deep: true }
);

// Filtering
const filtersFromVariables = computed(() => {
  if (!listViewParams.value.filter) return [];
  const filters = Object.keys(listViewParams.value.filter);
  pull(filters, 'types', 'listed');
  return filters;
});
const pagination = computed(() => goliathOffers.value?.pagination);

const hasOffers = computed(() => pagination.value?.totalCount > 0);

const showPagination = computed(() => hasOffers.value && pagination.value?.totalPages > 1);

const hasFilters = computed(() => filtersFromVariables.value?.length > 0);

const displayFiltersAndCount = computed(() => hasFilters.value || hasOffers.value);

const aggregations = computed(() => goliathOffers.value?.aggregations || {});

watch(aggregations, (currentValue, oldValue) => {
  if (
    !oldValue.brands &&
    props.couponBrands?.length > 0 &&
    aggregations.value?.brands?.length > 0 &&
    !aggregations.value?.brands?.some((item: any) => props.couponBrands?.includes(item.brand.slug))
  ) {
    store.dispatch('coupon/reportBrandError');
  }
});

const offersCount = computed(() => goliathOffers?.value?.variantsCount || 0);

const refreshOffers = (newVariables: any) => {
  userFiltersSet.value = true;
  listViewParams.value = {
    ...newVariables,
    filter: {
      ...newVariables.filter,
      articleNumbers: compatibleArticleNumbers.value,
      blacklistArticleNumbers: incompatibleArticleNumbers.value,
      productProviders:
        compatibleProductProviders.value?.length > 0
          ? compatibleProductProviders.value
          : newVariables.filter.productProviders,
      brandSlugs:
        props.couponBrands.length > 0 ? props.couponBrands : newVariables.filter.brandSlugs,
      productAvailable: true,
      discounted: isCouponType.value ? false : newVariables?.filter?.discounted || null,
    },
    pagination: listViewPagination.value,
  };

  listViewPagination.value.page = 1;
};

const pageNumber = (currentPage: number) => {
  let page: number | null = currentPage;
  if (!page || page <= 1) page = null;

  return page;
};

const pageLengthChanged = (size: number) => {
  listViewPagination.value.size = size;
  listViewPagination.value.page = 1;
};

const paginationChange = (targetPage: number) => {
  if (resultsEl.value) {
    window.scrollBy(0, resultsEl.value.getBoundingClientRect().top - 88); // fixed navbar height is 64px
  }
  listViewPagination.value.page = targetPage;
};

// Redeem coupon from url-query "/preisvorteile-sichern?code=ABC"
if (process.client && route?.query?.code) {
  try {
    if (!isValidCoupon.value) {
      submitCoupon(route.query.code as string);
    }
  } catch (e) {
    console.error(e);
  } finally {
    // remove code from url
    const { code, ...remainingQueryParams } = route.query;
    router.push({ path: route.path, query: remainingQueryParams });
  }
}

// necessary so that we have access to them in wrapper.vm in our unit tests
defineExpose({ submitCoupon, eraseCoupon, hiddenFilters });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.coupon-check {
  &__results {
    margin-bottom: 4 * $grid-base;
  }

  &__no-results {
    text-align: center;
    font-weight: 700;
    margin: $grid-base * 4 $grid-base $grid-base * 20;
    @include font-size(30px, 30px);
  }

  &__pagination-container {
    padding: $grid-base;
    grid-gap: $grid-base * 2;
    margin-block: $grid-base * 2.5;
    display: flex;
    flex-direction: column;

    @include mq($mq-md) {
      display: grid;
      grid-template-columns: 70px auto 70px;
      margin-bottom: $grid-base * 4;
    }
  }

  &__pagination {
    grid-column: 2;
  }

  &__page-length {
    text-align: center;
    align-self: end;
    grid-column: 3;
  }
}
</style>
